import { findParentNodeClosestToPos } from "@tiptap/core";
import { columnIsHeader, rowIsHeader, TableMap } from "@tiptap/pm/tables";

import { getCellAddress, isCellLike } from "@/components/tiptap/tables/utils";
import { getEquationParts } from "@/models/computedNode/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import { documentScope } from "./scopeExpressions";

import type { CellLocation, TableCellFormula } from "./types";
import type { CellPosition } from "@/components/tiptap/tables/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Editor } from "@tiptap/core";
import type { Node as ProsemirrorNode, ResolvedPos } from "@tiptap/pm/model";

/**
 * TODO: dedupe shared code with getPhantomHints (and other modules)
 */

/** The first `inlineExpression` node inside a table cell. */
function cellExpressionNode(cellNode: ProsemirrorNode): ProsemirrorNode | null {
  let found: ProsemirrorNode | null = null;
  cellNode.descendants((node) => {
    if (found) return false;
    if (node.type.name === "inlineExpression") {
      found = node;
      return false;
    }
    return undefined;
  });
  return found;
}

/** The math wrapper backing a table cell's expression (cell contents are flat wrappers). */
function cellWrapper(doc: DocumentModel, cellNode: ProsemirrorNode): WrapperNodeModel | null {
  const exprNode = cellExpressionNode(cellNode);
  if (!exprNode) return null;
  const wrapper = doc.nodes.get(exprNode.attrs.nodeId as string);
  return wrapper?.is(WrapperNodeModel) ? wrapper : null;
}

function cellNodes(doc: DocumentModel, cellNode: ProsemirrorNode): NodeModel[] {
  return cellWrapper(doc, cellNode)?.inlineNode?.nodes ?? [];
}

/**
 * A cell holding exactly one bare variable marks a data row.
 */
function cellVariableName(nodes: NodeModel[]): string | null {
  if (nodes.length !== 1) return null;
  const [node] = nodes;
  if (!node.is(VariableNodeModel) || node.hasParams) return null;
  return node.symbolNode?.labeledValue ?? null;
}

// TODO: merge with mentionsVariable()
function expressionUsesVariable(expression: NodeModel[], name: string): boolean {
  return expression.some((node) =>
    [node, ...node.descendants].some(
      (n) => n.is(VariableNodeModel) && n.symbolNode?.labeledValue === name,
    ),
  );
}

function locateCell($pos: ResolvedPos): CellLocation | null {
  const cell = findParentNodeClosestToPos($pos, isCellLike);
  const table = findParentNodeClosestToPos($pos, (node) => node.type.name === "table");
  if (!cell || !table) return null;

  const tableNode = table.node;
  const map = TableMap.get(tableNode);
  const cellIndex = map.map.indexOf(cell.pos - table.start);
  if (cellIndex < 0) return null;

  return {
    tableNode,
    map,
    tableStart: table.start,
    row: Math.floor(cellIndex / map.width),
    col: cellIndex % map.width,
    cellNode: cell.node,
  };
}

/**
 * A `CellLocation` for an arbitrary cell of an already-located table, so sibling cells the cursor
 * is not in (e.g. every data cell of a row) can be resolved too.
 */
function cellLocationAt(base: CellLocation, row: number, col: number): CellLocation | null {
  const cellNode = base.tableNode.nodeAt(base.map.map[row * base.map.width + col]);
  if (!cellNode) return null;
  return { ...base, row, col, cellNode };
}

/** Tiptap position inside the (empty) cell's paragraph: cell node → +1 into cell, +1 into paragraph. */
function cellContentPos(base: CellLocation, row: number, col: number): number {
  return base.tableStart + base.map.map[row * base.map.width + col] + 2;
}

/**
 * The table cell of every wrapper that sits in one, keyed by wrapper id, from a single pass over
 * the document.
 */
export function getWrapperCellPositions(tiptap: Editor): Map<string, CellPosition> {
  const positions = new Map<string, CellPosition>();

  tiptap.state.doc.descendants((node) => {
    if (node.type.name !== "table") return undefined;
    collectTable(node, positions);
    return false;
  });

  return positions;
}

/**
 * Addresses a table's expressions by walking its rows and cells. The column comes from the table
 * map rather than the cell's index in its row, since a spanning cell shifts the ones after it.
 */
function collectTable(table: ProsemirrorNode, positions: Map<string, CellPosition>): void {
  const map = TableMap.get(table);

  // A spanning cell fills several map slots; its address is the top-left one, the first seen.
  const cellIndexByOffset = new Map<number, number>();
  map.map.forEach((offset, index) => {
    if (!cellIndexByOffset.has(offset)) cellIndexByOffset.set(offset, index);
  });

  table.forEach((row, rowOffset) => {
    row.forEach((cell, cellOffset) => {
      if (!isCellLike(cell)) return;

      // Table map offsets are relative to the table's content: into the row, then to the cell.
      const cellIndex = cellIndexByOffset.get(rowOffset + 1 + cellOffset);
      if (cellIndex === undefined) return;

      const rowIndex = Math.floor(cellIndex / map.width);
      const col = cellIndex % map.width;
      const address = getCellAddress(rowIndex, col);

      cell.descendants((node) => {
        if (node.type.name === "table") {
          // A nested table addresses its expressions itself, as the innermost table.
          collectTable(node, positions);
          return false;
        }
        if (node.type.name === "inlineExpression") {
          positions.set(node.attrs.nodeId as string, { row: rowIndex, col, address });
          return false;
        }
        return undefined;
      });
    });
  });
}

/** Tiptap position of the `inlineExpression` node backing a wrapper, or -1 when absent. */
function findWrapperTiptapPos(tiptap: Editor, wrapperId: string): number {
  let found = -1;
  tiptap.state.doc.descendants((node, pos) => {
    if (found >= 0) return false;
    if (node.type.name === "inlineExpression" && node.attrs.nodeId === wrapperId) {
      found = pos;
      return false;
    }
    return undefined;
  });
  return found;
}

/**
 * A row header's formula — an assignment header contributes only its right-hand side.
 *
 * For example, `f = x + y` → `x + y`.
 */
function headerFormula(headerNodes: NodeModel[]): NodeModel[] {
  const [, rhs] = getEquationParts(headerNodes);
  return rhs ?? headerNodes;
}

/**
 * Resolve the columnar formula for a data cell: its row header supplies the expression, and every
 * *other* row of the cell's column contributes `row header = that row's value` to the scope.
 * Does NOT inspect the cell's own contents — callers decide whether an empty cell is required.
 */
function resolveCellFormula(
  doc: DocumentModel,
  tiptap: Editor,
  loc: CellLocation,
): TableCellFormula | null {
  const { tableNode, map, row, col } = loc;

  if (!columnIsHeader(map, tableNode, 0)) return null;

  const skipHeaderRow = rowIsHeader(map, tableNode, 0);

  if (col === 0) return null;
  if (skipHeaderRow && row === 0) return null;

  const headerCell = tableNode.nodeAt(map.map[row * map.width]);
  if (!headerCell) return null;

  const headerNodes = cellNodes(doc, headerCell);
  if (headerNodes.length === 0) return null;

  const expression = headerFormula(headerNodes);
  if (expression.length === 0) return null;

  // `tableStart` is the table's content start, so the table node itself sits one position earlier.
  const scope = documentScope(doc, tiptap, loc.tableStart - 1);

  // Column rows are appended last: they shadow same-named definitions from outside the table.
  for (let r = 0; r < map.height; r++) {
    if (r === row || (skipHeaderRow && r === 0)) continue;

    const rowHeader = tableNode.nodeAt(map.map[r * map.width]);
    const rowHeaderNodes = rowHeader ? cellNodes(doc, rowHeader) : [];
    const lhs = headerFormula(rowHeaderNodes);
    if (lhs.length === 0) continue;

    const valueCell = tableNode.nodeAt(map.map[r * map.width + col]);
    const valueWrapper = valueCell ? cellWrapper(doc, valueCell) : null;
    const valueNodes = valueWrapper?.inlineNode?.nodes ?? [];
    if (valueNodes.length === 0) {
      // ignore rows that don't mention used variables
      const unbound = cellVariableName(rowHeaderNodes);
      if (unbound && expressionUsesVariable(expression, unbound)) return null;
      continue;
    }

    scope.push({ wrapperId: valueWrapper!.id, lhs, rhs: valueNodes });
  }

  return { expression, scope };
}

/**
 * The columnar formula for the cell the cursor sits in, when that cell is empty. Drives the
 * phantom-hint suggestion into a blank cell.
 *
 * For example, cursor in the last cell evaluates `x*y` with x=2, y=4:
 * ```
 * | x   | 1 | 2 |
 * | y   | 3 | 4 |
 * | x*y |   | ← |
 * ```
 */
export function getTableCellFormulaContext(
  doc: DocumentModel,
  tiptap: Editor,
): TableCellFormula | null {
  const loc = locateCell(tiptap.state.selection.$head);
  if (!loc) return null;
  if (cellNodes(doc, loc.cellNode).length > 0) return null;
  return resolveCellFormula(doc, tiptap, loc);
}

/**
 * The columnar formula for a wrapper's cell, ignoring the cell's own content (which, once computed,
 * holds the result). Used by the computed-node manager to (re)evaluate a computed table cell.
 */
export function getTableCellComputation(
  doc: DocumentModel,
  tiptap: Editor,
  wrapperId: string,
): TableCellFormula | null {
  const pos = findWrapperTiptapPos(tiptap, wrapperId);
  if (pos < 0) return null;
  const loc = locateCell(tiptap.state.doc.resolve(pos));
  return loc ? resolveCellFormula(doc, tiptap, loc) : null;
}

/** True when the wrapper backs a computable data cell (gates computed-node behavior). */
export function isComputableTableCell(
  doc: DocumentModel,
  tiptap: Editor,
  wrapperId: string,
): boolean {
  return getTableCellComputation(doc, tiptap, wrapperId) !== null;
}

/** One data cell of a fully-empty formula row: the columnar formula plus where to place its hint. */
export type TableRowHintCell = TableCellFormula & { contentPos: number };

/** True when the cursor sits in a row-header cell (column 0), whatever that cell holds. */
export function isRowHeader(tiptap: Editor): boolean {
  const loc = locateCell(tiptap.state.selection.$head);
  if (!loc) return false;

  const { tableNode, map, row } = loc;
  if (loc.col !== 0) return false;
  if (!columnIsHeader(map, tableNode, 0)) return false;
  if (rowIsHeader(map, tableNode, 0) && row === 0) return false;

  return true;
}

/**
 * A non-empty row header, whether or not its data row is filled. Such a header drives the columnar
 * row hints, so the idle "=" inline flow must stay out of it.
 */
export function isFormulaRowHeader(doc: DocumentModel, tiptap: Editor): boolean {
  if (!isRowHeader(tiptap)) return false;

  const loc = locateCell(tiptap.state.selection.$head);
  if (!loc) return false;

  const headerNodes = cellNodes(doc, loc.cellNode);
  return headerNodes.length > 0;
}

/**
 * When the cursor sits in a row header and *every* data cell in that row is empty, return one entry
 * per data column: that column's formula plus the tiptap position at which to render / insert the
 * hint. Returns null unless the whole data row is blank and computable.
 *
 * For example, cursor in the `x*y` header cell hints `3` (x=1, y=3) and `8` (x=2, y=4):
 * ```
 * | x   | 1 | 2 |
 * | y   | 3 | 4 |
 * | x*y | ← | ← |
 * ```
 */
export function getTableRowFormulaCells(
  doc: DocumentModel,
  tiptap: Editor,
): TableRowHintCell[] | null {
  if (!isFormulaRowHeader(doc, tiptap)) return null;

  const loc = locateCell(tiptap.state.selection.$head);
  if (!loc) return null;

  const { row, map } = loc;

  const cells: TableRowHintCell[] = [];
  for (let col = 1; col < map.width; col++) {
    const cellLoc = cellLocationAt(loc, row, col);
    if (!cellLoc) return null;
    if (cellNodes(doc, cellLoc.cellNode).length > 0) return null;
    const formula = resolveCellFormula(doc, tiptap, cellLoc);
    if (formula) {
      cells.push({ ...formula, contentPos: cellContentPos(loc, row, col) });
    }
  }

  return cells.length > 0 ? cells : null;
}

/**
 * A stable key for phantom-hint watcher, non-null only where columnar hints can apply.
 */
export function tableCellHintKey(doc: DocumentModel, tiptap: Editor): string | null {
  const { from, to } = doc.editor.textSelection;
  if (getTableCellFormulaContext(doc, tiptap)) return `table:cell:${from}:${to}`;
  if (isRowHeader(tiptap)) return `table:row:${from}:${to}`;
  return null;
}
